import React from "react";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Input } from "../../common/components/Input";
import { spacing } from "../../styles/theme";
import { SpecsAssetImage } from "./SpecsEditorFields/index";
import { formatSpecsInheritedPlaceholder } from "./specsInheritedPlaceholder";
import { getAssetPreviewUrl, uniqueStrings } from "./specsEditorUtils";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useProductVersionMetadataModel } from "./useProductVersionMetadataModel";

export default function SpecsStylesPage() {
  const {
    currentVersion,
    layerDisplayGroups,
    editor: { identity, rawVersionMetadata, updateRawVersionMetadata },
    modals,
    addStyle,
    removeStyle,
    updateStyle,
    updateStyleIcon,
  } = useSpecsEditorContextValue();
  const metadataModel = useProductVersionMetadataModel({
    rawVersionMetadata,
    updateRawVersionMetadata,
  });
  const groupStudioLabelKeys = metadataModel.studioLabelKeys?.groups ?? {};
  const uniqueLayerDisplayGroups = uniqueStrings(layerDisplayGroups);
  const displayableElements = currentVersion
    ? currentVersion.parts.flatMap((part) =>
        part.elements
          .filter((element) => element.displayable)
          .map((element) => ({ part, element }))
      )
    : [];

  const setGroupStudioLabelKey = (groupName: string, value: string) => {
    const nextGroups = { ...groupStudioLabelKeys };
    if (value.trim()) {
      nextGroups[groupName] = value;
    } else {
      delete nextGroups[groupName];
    }
    metadataModel.studioLabelKeys!.groups = nextGroups;
  };

  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Typography variant="h2" sx={{ mb: 3 }}>
        Styles
      </Typography>

      <Stack spacing={3}>
        <Box>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Clés de libellés de groupes Studio
          </Typography>
          {uniqueLayerDisplayGroups.length > 0 ? (
            <Stack spacing={2}>
              {uniqueLayerDisplayGroups.map((groupName: string) => (
                <Input
                  key={groupName}
                  label={groupName}
                  debugPath={`window.specs.metadataModel.studioLabelKeys.groups.${groupName}`}
                  noMaxWidth
                  fullWidth
                  value={groupStudioLabelKeys[groupName] || ""}
                  onChange={(e) =>
                    setGroupStudioLabelKey(groupName, e.target.value)
                  }
                  placeholder={formatSpecsInheritedPlaceholder(
                    `specs.${identity.productName}.${groupName}`
                  )}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="info">
              Assign layer display groups to configure Studio group label keys.
            </Alert>
          )}
        </Box>

        <Box>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Element Styles
          </Typography>
          {displayableElements.length === 0 ? (
            <Alert severity="info">Add elements to configure styles.</Alert>
          ) : (
            <Stack spacing={2.5}>
              {displayableElements.map(({ part, element }) => (
                <Box
                  key={`${part.name}:${element.name}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "minmax(180px, 0.8fr) minmax(0, 1fr)",
                    },
                    gap: 2,
                    pb: 2.5,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                    "&:last-of-type": { borderBottom: "none", pb: 0 },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {element.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {part.name}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      sx={{ mt: 1 }}
                      onClick={() =>
                        modals.openPromptDialog(
                          "Add Style",
                          "Style name",
                          "",
                          (name) => addStyle(element.name, name, part.name)
                        )
                      }
                    >
                      Add style
                    </Button>
                  </Box>

                  <Stack spacing={1.5}>
                    {element.styles.map((style: any) => (
                      <Box
                        key={style.name}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(120px, 1fr) auto",
                          },
                          gap: 1.5,
                          alignItems: "center",
                          p: 1.5,
                          border: "1px solid rgba(0,0,0,0.08)",
                          borderRadius: "6px",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          {style.menuIconUrl ? (
                            <SpecsAssetImage
                              src={getAssetPreviewUrl(
                                style.displayExtra,
                                "menuIconUrl",
                                style.menuIconUrl
                              )}
                              alt=""
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: "contain",
                              }}
                            />
                          ) : null}
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700 }}
                            >
                              {style.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.secondary" }}
                            >
                              {element.defaultStyle === style.name
                                ? "Style par défaut"
                                : "Style"}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          gap={1}
                          justifyContent={{ xs: "flex-start", md: "flex-end" }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              modals.openPromptDialog(
                                "Rename Style",
                                "New style name",
                                style.name,
                                (newName) => {
                                  if (newName !== style.name) {
                                    updateStyle(
                                      element.name,
                                      style.name,
                                      newName,
                                      part.name
                                    );
                                  }
                                }
                              )
                            }
                          >
                            Renommer
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              modals.openIconPicker({
                                kind: "style",
                                partName: part.name,
                                elementName: element.name,
                                styleName: style.name,
                              })
                            }
                          >
                            {style.menuIconUrl
                              ? "Changer l'icône"
                              : "Ajouter une icône"}
                          </Button>
                          {style.menuIconUrl ? (
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() =>
                                updateStyleIcon(
                                  element.name,
                                  style.name,
                                  "",
                                  part.name
                                )
                              }
                            >
                              Supprimer l'icône
                            </Button>
                          ) : null}
                          {element.styles.length > 1 ? (
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() =>
                                removeStyle(element.name, style.name, part.name)
                              }
                            >
                              Supprimer
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
